import { Flex, Skeleton } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { memo, useEffect, useRef } from "react";

import { useStores } from "@stores";
import { formatDuration } from "@utils/Format";
import { openMarketExplorer } from "@utils/openExplorer";

import { Icon } from "@components/Icon";
import { useCountdown } from "@components/hooks/useCountdown";
import { Button, Text } from "@src/ui-kit";

import type { TFunction } from "i18next";
import { DataContainer, DataContainerMobile, DataLabel, MonoText } from "./styles";

// Memoized base item component - completely static, no observer dependencies
export const MarketItemBase = memo<{
  testId: string;
  label: string;
  children: React.ReactNode;
  mobile?: boolean;
}>(({ testId, label, children, mobile }) => {
  const Container = mobile ? DataContainerMobile : DataContainer;
  return (
    <Container data-testid={`market-data:item:${testId}`}>
      <DataLabel>{label}</DataLabel>
      {children}
    </Container>
  );
});

// NextFundingTime - observer component that handles countdown independently
export const NextFundingTime = observer(({ time }: { time: Date }) => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;
  const timeToNextFunding = useCountdown(time, true);
  const hasLoadedRef = useRef(false);
  const previousMarketAddr = useRef<string | null>(null);
  const previousTimeRef = useRef<number>(0);

  useEffect(() => {
    if (market?.addr !== previousMarketAddr.current) {
      hasLoadedRef.current = false;
      previousMarketAddr.current = market?.addr ?? null;
      previousTimeRef.current = timeToNextFunding;
    }

    const wasPositive = previousTimeRef.current > 0;
    const becameZero = timeToNextFunding <= 0 && wasPositive;

    if (becameZero && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      // Load fresh tickers from REST API when countdown reaches 0
      void marketStore.loadTickers();
    }

    previousTimeRef.current = timeToNextFunding;
  }, [timeToNextFunding, market?.addr]);

  const formattedTime = formatDuration(timeToNextFunding, "HH:mm:ss");
  return (
    <MonoText data-testid="market-data:funding-rate:countdown" font="body14">
      {formattedTime}
    </MonoText>
  );
});

// Funding Rate display - isolated observer
export const FundingRateDisplay = observer(() => {
  const { marketStore } = useStores();
  const market = marketStore.activeMarket;
  const marketData = marketStore.getMarketData(market?.addr ?? "");

  const fundingRate = Number(market?.funding_rate_percentage) || 0;
  const isFundingPositive = fundingRate >= 0;

  return (
    <Skeleton height="18px" loading={!market} width="130px">
      <Flex align="center" gap="4px" minWidth="130px">
        <MonoText
          data-testid="market-data:funding-rate:value"
          color={isFundingPositive ? "green-strong" : "red-strong"}
          font="body14"
        >
          {isFundingPositive && "+"}
          {fundingRate.toFixed(6)}%
        </MonoText>
        <Text font="body14">/</Text>
        <NextFundingTime time={marketData.nextFundingTime} />
      </Flex>
    </Skeleton>
  );
});

// Market Address Button - uses stable activeMarketAddr computed (doesn't re-render on ticker updates)
export const MarketAddressButton = observer(({ t, size }: { t: TFunction<"translation", undefined>; size?: "2" }) => {
  const { marketStore } = useStores();
  // Use stable computed - only changes when market actually changes, not on price updates
  const addr = marketStore.activeMarketAddr;

  if (!addr) return null;

  return (
    <Button
      data-testid="market-data:market-address"
      size={size}
      variant="classic"
      onClick={() => openMarketExplorer(addr)}
    >
      {t("market.marketAddress")}
      <Icon icon="ri-external-link-fill" size="12px" />
    </Button>
  );
});
